// =============================================================================
// GeninieChatPanel.jsx
// -----------------------------------------------------------------------------
// Reusable presentation layer for the Eligibility Agent chat panel.
// Eligibility state, API calls, and callbacks remain owned by the parent page
// so moving this UI does not alter the existing eligibility workflow.
// =============================================================================

import React, { useEffect, useRef } from "react";
// Shared animated assistant placeholder for delayed onboarding messages.
import GenieTypingIndicator from "./GenieTypingIndicator";

const PROFILE_CONFIRM_STEPS = {
  "Please review and confirm your Company Info on the right, then click Confirm.": 1,
  "Please review and confirm your Team Info on the right, then click Confirm.": 2,
  "Please review and confirm the Grant Info on the right, then click Confirm.": 3,
};

const GeninieChatPanel = ({
  activeChatTab,
  onActiveChatTabChange,
  currentStep,
  hasConfirmedGrantInfo,
  grantUrl,
  eligibilityLoading,
  eligibilityError,
  eligibilityResult,
  clarifications,
  proposalPromptAnswer,
  onAnswerUncertainty,
  onProposalPromptAction,
  // Profile chat history contains the inherited onboarding messages plus each
  // delayed step instruction. It is owned by the parent page.
  chatMessages = [],
  // MAIN HISTORY MODE: The grant-assistant page displays the completed
  // eligibility conversation, but must not submit duplicate agent actions.
  readOnly = false,
  // HEADING CONTROL: The profile form retains its Eligibility heading. The
  // proposal page can pass a non-breaking space to preserve this layout slot.
  showHeading = true,
  headingText = "Eligibility",
  // Onboarding mode supplies local instructional messages only; it never calls
  // the eligibility agent and is intended for CheckEligibility.
  onboardingMessages,
  isTyping = false,
  onConfirmStep,
  confirmedChatSteps = [],
  // The /form page reserves a footer beneath the Eligibility conversation for
  // a future search action. Other consumers keep their existing layout.
  showSearchButton = false,
}) => {
  // ---------------------------------------------------------------------------
  // AUTO-SCROLL: Keep the latest assistant content and typing indicator visible.
  // ---------------------------------------------------------------------------
  const chatContainerRef = useRef(null);
  const chatBottomRef = useRef(null);

  useEffect(() => {
    // Copilot has no conversation updates to follow, so preserve its position.
    if (activeChatTab !== "eligibility") return;

    // Wait for React to commit conditional result/typing content before moving.
    const frameId = window.requestAnimationFrame(() => {
      chatBottomRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [
    activeChatTab,
    chatMessages,
    onboardingMessages,
    isTyping,
    eligibilityLoading,
    eligibilityError,
    eligibilityResult,
    clarifications,
    proposalPromptAnswer,
  ]);

  // ---------------------------------------------------------------------------
  // ONBOARDING VARIANT: compact, agent-free instructional chat.
  // ---------------------------------------------------------------------------
  if (onboardingMessages) {
    // Fill the onboarding gradient frame while keeping this flow entirely local.
    return (
      <aside className="flex h-full min-h-0 w-full flex-col bg-[#0E0126]">
        {/* Reuse the shared tabs without an additional onboarding heading. */}
        <div className="chat-tabs">
          <button
            type="button"
            className={`chat-tab-btn ${
              activeChatTab === "eligibility" ? "active" : ""
            }`}
            onClick={() => onActiveChatTabChange("eligibility")}
          >
            Genie
          </button>
          <button
            type="button"
            className={`chat-tab-btn ${
              activeChatTab === "copilot" ? "active" : ""
            }`}
            onClick={() => onActiveChatTabChange("copilot")}
          >
            Copilot
          </button>
        </div>

        {activeChatTab === "eligibility" ? (
          <div ref={chatContainerRef} className="chat-container custom-scrollbar px-4 py-4">
            {/* Messages are supplied as an accumulated history by the parent. */}
            <div className="space-y-4">
              {onboardingMessages.map((message) => (
                <div key={message} className="flex justify-start">
                  <div className="bg-[#412293] px-4 py-3 rounded-2xl rounded-tl-none text-[15px] leading-relaxed text-white max-w-[90%] shadow-md">
                    {message}
                  </div>
                </div>
              ))}
              {/* Temporary assistant response shown between queued prompts. */}
              {isTyping && <GenieTypingIndicator />}
              {/* Stable target used to scroll newly rendered messages into view. */}
              <div ref={chatBottomRef} aria-hidden="true" />
            </div>
          </div>
        ) : (
          <div className="chat-container p-8 text-center text-gray-500">
            <p className="text-sm">Copilot features Should be moved here</p>
          </div>
        )}
      </aside>
    );
  }

  // ---------------------------------------------------------------------------
  // GRANT PROFILE VARIANT: existing eligibility-agent UI.
  // ---------------------------------------------------------------------------
  return (
  <aside className="chat-panel">
    {/* HEADING CONTROL: Keep the form heading, but let Main use its proposal title. */}
    {showHeading && (
      <div
        style={{
          marginTop: "18px",
          marginBottom: "16px",
          paddingLeft: "16px",
          fontFamily: "Roboto, sans-serif",
          fontSize: "22px",
          fontWeight: "bold",
          color: "white",
        }}
      >
        {headingText}
      </div>
    )}

    {/* Shared Genie / Copilot tab controls. */}
    <div className="chat-tabs">
      <button
        className={`chat-tab-btn ${
          activeChatTab === "eligibility" ? "active" : ""
        }`}
        onClick={() => onActiveChatTabChange("eligibility")}
      >
        Genie
      </button>
      <button
        className={`chat-tab-btn ${
          activeChatTab === "copilot" ? "active" : ""
        }`}
        onClick={() => onActiveChatTabChange("copilot")}
      >
        Copilot
      </button>
    </div>

    <div ref={chatContainerRef} className="chat-container custom-scrollbar">
      {activeChatTab === "eligibility" ? (
        <>
          {/* The parent supplies inherited onboarding and delayed step messages in order. */}
          <div className="px-4 space-y-4 mt-4 mb-4">
            {chatMessages.map((message, index) => {
              const confirmStep = PROFILE_CONFIRM_STEPS[message];
              const showConfirm =
                confirmStep === currentStep &&
                !confirmedChatSteps.includes(confirmStep) &&
                onConfirmStep;

              return (
                <div key={`${index}-${message}`} className="flex flex-col gap-3">
                  <div className="flex justify-start">
                    {/* ALIGNMENT: Genie instruction/history bubbles always remain left aligned. */}
                    <div className="bg-[#412293] px-4 py-3 rounded-2xl rounded-tl-none text-left text-[15px] leading-relaxed text-white max-w-[90%] shadow-md">
                      {message}
                    </div>
                  </div>
                  {showConfirm && (
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={onConfirmStep}
                        className="bg-[#8749E7] px-4 py-1 rounded-full text-xs text-white capitalize cursor-pointer transition-colors hover:bg-[#a66cff]"
                      >
                        Confirm
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
            {/* Genie remains visible for the full parent-controlled two-second delay. */}
            {isTyping && <GenieTypingIndicator />}
          </div>

          {hasConfirmedGrantInfo && (
            <>
              {/* Eligibility status is intentionally gated by grant-info confirmation. */}
              <div className="checking-url-bar">
                <span className="font-bold">Checking URL: </span>
                {grantUrl || "No URL provided"}
              </div>

              {eligibilityLoading && (
                <div className="px-4 py-2 text-gray-400 italic">
                  Analyzing eligibility...
                </div>
              )}

              {eligibilityError && (
                <div className="mx-4 mb-4 p-3 bg-red-900/40 text-red-200 text-xs rounded border border-red-800">
                  {eligibilityError}
                </div>
              )}

              {eligibilityResult && (
                <div className="eligibility-card shadow-lg">
                  <p className="font-bold text-lg mb-2 text-center border-b border-white/10 pb-2">
                    Eligibility:{" "}
                    {eligibilityResult.eligible === true
                      ? "Eligible"
                      : eligibilityResult.eligible === false
                        ? "Not Eligible"
                        : "Uncertain"}
                  </p>
                  <p className="font-bold text-sm mb-4 text-center">
                    AI Certainty:{" "}
                    <span
                      className={
                        eligibilityResult.confidence === "High"
                          ? "text-green-400"
                          : eligibilityResult.confidence === "Medium"
                            ? "text-yellow-400"
                            : "text-red-400"
                      }
                    >
                      {eligibilityResult.confidence}
                    </span>
                  </p>

                  {eligibilityResult.reasons?.length > 0 && (
                    <div className="mb-4">
                      <p className="font-semibold text-xs uppercase text-purple-300 mb-1">
                        Breakdown:
                      </p>
                      <ul className="list-disc pl-5 text-sm space-y-1">
                        {eligibilityResult.reasons.map((reason, index) => (
                          <li key={index}>{reason}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {eligibilityResult.source_pages?.length > 0 && (
                    <div>
                      {/* STATUS CARD ALIGNMENT: Keep the source heading centered,
                          while individual URL links remain left aligned. */}
                      <p className="font-semibold text-xs uppercase text-purple-300 mb-1 text-center">
                        Source Pages Scraped:
                      </p>
                      <ul className="list-disc pl-5 text-left text-[10px] text-blue-300 space-y-1">
                        {eligibilityResult.source_pages.map((page, index) => (
                          <li key={index} className="break-all italic">
                            <a
                              href={page}
                              target="_blank"
                              rel="noreferrer"
                              className="hover:underline"
                            >
                              {page}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Clarification history, pending question, and proposal prompt. */}
              <div className="px-4 space-y-4">
                {Object.entries(clarifications).map(([question, answer], index) => (
                  <div key={index} className="flex flex-col gap-2">
                    <div className="flex justify-start">
                      {/* ALIGNMENT: Clarification questions are Genie bubbles, not status labels. */}
                      <div className="bg-[#412293] px-4 py-2 rounded-2xl rounded-tl-none text-left text-sm text-white max-w-[90%]">
                        {question}
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <div className="bg-[#8749E7] px-4 py-1 rounded-full rounded-tr-none text-xs text-white capitalize">
                        {answer}
                      </div>
                    </div>
                  </div>
                ))}

                {(() => {
                  // READ-ONLY HISTORY: Keep unanswered questions visible after
                  // navigation, while preventing duplicate eligibility requests.
                  if (!eligibilityResult?.uncertainties || eligibilityLoading) {
                    return null;
                  }

                  const pendingUncertainties =
                    eligibilityResult.uncertainties.filter((uncertainty) => {
                      const question =
                        typeof uncertainty === "string"
                          ? uncertainty
                          : uncertainty.question;
                      return !clarifications[question];
                    });

                  if (pendingUncertainties.length === 0) return null;

                  const uncertainty = pendingUncertainties[0];
                  const question =
                    typeof uncertainty === "string"
                      ? uncertainty
                      : uncertainty.question;

                  return (
                    <div className="flex flex-col gap-2">
                      <div className="flex justify-start">
                        {/* ALIGNMENT: Clarification questions are Genie bubbles, not status labels. */}
                        <div className="bg-[#412293] px-4 py-2 rounded-2xl rounded-tl-none text-left text-sm text-white max-w-[90%]">
                          {question}
                        </div>
                      </div>
                      {!readOnly && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => onAnswerUncertainty?.(question, true)}
                            className="bg-[#5406D2] px-4 py-1 rounded-full text-xs text-white hover:bg-[#6d60eb]"
                          >
                            Yes
                          </button>
                          <button
                            onClick={() => onAnswerUncertainty?.(question, false)}
                            className="bg-white/10 px-4 py-1 rounded-full text-xs text-white hover:bg-white/20"
                          >
                            No
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {eligibilityResult &&
                  !eligibilityLoading &&
                  eligibilityResult.eligible === true && (
                    <div className="flex flex-col gap-3">
                      <div className="flex justify-start">
                        <div className="bg-[#412293] px-4 py-2 rounded-2xl rounded-tl-none text-left text-sm text-white">
                          Want to start the grant proposal?
                        </div>
                      </div>
                      {proposalPromptAnswer === null ? (
                        !readOnly && (
                          <div className="flex gap-2">
                            <button
                              onClick={() => onProposalPromptAction?.("yes")}
                              className="bg-[#5406D2] px-4 py-1 rounded-full text-xs text-white hover:bg-[#6d60eb]"
                            >
                              Yes
                            </button>
                            <button
                              onClick={() => onProposalPromptAction?.("no")}
                              className="bg-white/10 px-4 py-1 rounded-full text-xs text-white hover:bg-white/20"
                            >
                              No
                            </button>
                          </div>
                        )
                      ) : (
                        <div className="flex justify-end">
                          <div className="bg-[#8749E7] px-4 py-1 rounded-full rounded-tr-none text-xs text-white capitalize">
                            {proposalPromptAnswer}
                          </div>
                        </div>
                      )}
                      {proposalPromptAnswer === "yes" && (
                        <div className="flex justify-start">
                          <div className="bg-[#412293] px-4 py-2 rounded-2xl rounded-tl-none text-left text-sm text-white">
                            Starting proposal...
                          </div>
                        </div>
                      )}
                    </div>
                  )}
              </div>
            </>
          )}
          {/* Stable target used to scroll eligibility updates into view. */}
          <div ref={chatBottomRef} aria-hidden="true" />
        </>
      ) : (
        /* Copilot remains the same placeholder currently used by the page. */
        <div className="p-8 text-center text-gray-500">
          <p className="text-sm">Copilot features Should be moved here</p>
        </div>
      )}
    </div>

    {showSearchButton && activeChatTab === "eligibility" && (
      <div className="eligibility-search-area">
        <div
          className={`eligibility-analysis-status ${eligibilityLoading ? "analyzing" : ""}`}
          role="status"
          aria-live="polite"
          aria-label={eligibilityLoading ? "Analyzing eligibility" : "Eligibility agent idle"}
        >
          {eligibilityLoading ? "Analyzing..." : "\u00a0"}
        </div>
      </div>
    )}

  </aside>
);
};

export default GeninieChatPanel;
